/** Relative presets a list filter offers, each covering "since N ago".
 *
 * The upper bound is always open; only a custom range bounds both ends. */
const DATE_RANGE_VALUES = ["all", "1h", "24h", "7d", "30d", "90d"] as const;

export type DateRangeValue = (typeof DATE_RANGE_VALUES)[number];

export const DATE_RANGE_OPTIONS: Array<{
  label: string;
  value: DateRangeValue;
}> = [
  { label: "All time", value: "all" },
  { label: "Last hour", value: "1h" },
  { label: "Last 24 hours", value: "24h" },
  { label: "Last 7 days", value: "7d" },
  { label: "Last 30 days", value: "30d" },
  { label: "Last 90 days", value: "90d" },
];

const DURATION_MS: Record<Exclude<DateRangeValue, "all">, number> = {
  "1h": 60 * 60 * 1000,
  "24h": 24 * 60 * 60 * 1000,
  "7d": 7 * 24 * 60 * 60 * 1000,
  "30d": 30 * 24 * 60 * 60 * 1000,
  "90d": 90 * 24 * 60 * 60 * 1000,
};

/**
 * Convert a preset date range value to an ISO 8601 start_time string.
 * Returns undefined for "all" (no time filter).
 */
export function dateRangeToStartTime(
  value: DateRangeValue
): string | undefined {
  if (value === "all") {
    return;
  }

  return new Date(Date.now() - DURATION_MS[value]).toISOString();
}

/** The value a filter carries when the caller has named both bounds itself. */
export const CUSTOM_DATE_RANGE = "custom";

export const DATE_RANGE_WITH_CUSTOM_VALUES = [
  ...DATE_RANGE_VALUES,
  CUSTOM_DATE_RANGE,
] as const;

export type DateRangeWithCustom =
  (typeof DATE_RANGE_WITH_CUSTOM_VALUES)[number];

/** The three fields a time-window filter carries in the URL. */
export type TimeRangeFilterState = {
  dateRange: DateRangeWithCustom;
  startTime: string;
  endTime: string;
};

/** Wire params for a bounded read. Either bound may be absent. */
export type TimeWindow = { start_time?: string; end_time?: string };

export const isDateRangePreset = (
  value: DateRangeWithCustom
): value is DateRangeValue => value !== CUSTOM_DATE_RANGE;

const trimmedOrUndefined = (value: string): string | undefined => {
  const next = value.trim();
  return next === "" ? undefined : next;
};

/**
 * A preset covers "since N ago" and leaves the upper bound open; only a custom
 * range can bound both ends, and either of its bounds may be left blank.
 */
export function resolveTimeWindow({
  dateRange,
  startTime,
  endTime,
}: TimeRangeFilterState): TimeWindow {
  if (isDateRangePreset(dateRange)) {
    const start = dateRangeToStartTime(dateRange);
    return start ? { start_time: start } : {};
  }

  const start = trimmedOrUndefined(startTime);
  const end = trimmedOrUndefined(endTime);

  return {
    ...(start !== undefined && { start_time: start }),
    ...(end !== undefined && { end_time: end }),
  };
}
